import React, { useState } from "react";
import styles from "./home.module.css";
import { Col, Row, Form, Button } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import Header from "../../Component/Header/Header";
import { Helmet } from "react-helmet";
import { CREATE_FORMS } from "../../APIs/BaseUrl";
import Notification from "../../Component/Notification/Notification";
import { useAuth } from "../../Context/AuthContext";

const questionTypes = [
    { value: "text", label: "Text" },
    { value: "number", label: "Number" },
    { value: "date", label: "Date" },
    { value: "multiple-choice", label: "Multiple Choice" },
    { value: "dropdown", label: "Dropdown" },
    { value: "file", label: "File" },
];

const createEmptyQuestion = () => ({
    questionText: "",
    questionText_i18n: { en: "", hinglish: "" },
    questionType: "text",
    required: true,
    optionRows: [],
});

const buildQuestionPayload = (question) => {
    const englishText =
        question.questionText_i18n?.en?.trim() || question.questionText?.trim() || "";
    const hinglishText = question.questionText_i18n?.hinglish?.trim() || "";
    const isChoiceType = ["dropdown", "multiple-choice"].includes(question.questionType);

    const optionRows = isChoiceType
        ? (question.optionRows || []).map((option) => ({
              en: option.en?.trim() || "",
              hinglish: option.hinglish?.trim() || "",
          }))
        : [];

    return {
        questionText: englishText,
        questionText_i18n: {
            en: englishText,
            hinglish: hinglishText,
        },
        questionType: question.questionType,
        required: question.required !== false,
        options: optionRows.map((option) => option.en).filter(Boolean),
        options_i18n: optionRows.filter((option) => option.en || option.hinglish),
    };
};

const CreateForm = () => {
    const navigate = useNavigate();
      const { toggleNotification } = useAuth();

    const [formName, setFormName] = useState("");
    const [questions, setQuestions] = useState([createEmptyQuestion()]);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);
    const [loading, setLoading] = useState(false);

    const handleQuestionChange = (idx, field, value) => {
        const updated = [...questions];
        if (field === "questionTextEn") {
            updated[idx].questionText = value;
            updated[idx].questionText_i18n = {
                ...(updated[idx].questionText_i18n || {}),
                en: value,
            };
        } else if (field === "questionTextHinglish") {
            updated[idx].questionText_i18n = {
                ...(updated[idx].questionText_i18n || {}),
                hinglish: value,
            };
        } else {
            updated[idx][field] = value;
        }

        if (field === "questionType" && !["dropdown", "multiple-choice"].includes(value)) {
            updated[idx].optionRows = [];
        }
        setQuestions(updated);
    };

    const handleOptionChange = (qIdx, oIdx, field, value) => {
        const updated = [...questions];
        updated[qIdx].optionRows[oIdx][field] = value;
        setQuestions(updated);
    };

    const addOption = (qIdx) => {
        const updated = [...questions];
        updated[qIdx].optionRows.push({ en: "", hinglish: "" });
        setQuestions(updated);
    };

    const removeOption = (qIdx, oIdx) => {
        const updated = [...questions];
        updated[qIdx].optionRows.splice(oIdx, 1);
        setQuestions(updated);
    };

    const addQuestion = () => {
        setQuestions([...questions, createEmptyQuestion()]);
    };

    const removeQuestion = (idx) => {
        const updated = [...questions];
        updated.splice(idx, 1);
        setQuestions(updated);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError(null);
        setSuccess(null);
        setLoading(true);
        try {
            const preparedQuestions = questions.map(buildQuestionPayload);

            for (const question of preparedQuestions) {
                if (!question.questionText) {
                    throw new Error("Each question must have English text.");
                }

                if (["dropdown", "multiple-choice"].includes(question.questionType)) {
                    if (question.options.length < 2) {
                        throw new Error("Dropdown and multiple-choice questions need at least 2 options.");
                    }
                }
            }

            const token = localStorage.getItem("token");
            const response = await fetch(CREATE_FORMS, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify({
                    form_name: formName,
                    questions: preparedQuestions,
                }),
            });

            const data = await response.json();
            if (!data.success) throw new Error(data.message || "Failed to create form");
            
            setSuccess("Form created successfully!");   
            setTimeout(() => navigate("/forms"), 1200);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    return (
        <React.Fragment>
            <div className="right-aside">
                <Helmet>Create Form</Helmet>
                <Row>
                    <Col md={!toggleNotification ? 12 : 8} sm={12}>
                        <Header />
                        <div className={styles.head_sec}>
                            <div className="d-flex justify-content-between align-items-center mb-3">
                                <h6 className="mb-0">Create Form</h6>
                                <Button
                                    variant="secondary"
                                    size="sm"
                                    type="button"
                                    onClick={() => navigate("/forms")}
                                >
                                    Back to Forms
                                </Button>
                            </div>
                            {error && <div className="text-danger mb-2">{error}</div>}
                            {success && <div className="text-success mb-2">{success}</div>}
                            <Form onSubmit={handleSubmit}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Form Name</Form.Label>
                                    <Form.Control
                                        type="text"
                                        value={formName}
                                        onChange={(e) => setFormName(e.target.value)}
                                        required
                                    />
                                </Form.Group>
                                <hr />
                                <h6>Questions</h6>
                                {questions.map((q, idx) => (
                                    <div key={idx} className={styles.questionCard}>
                                        <div className="d-flex justify-content-between align-items-center mb-3">
                                            <span className={styles.questionTitle}>Question {idx + 1}</span>
                                            {questions.length > 1 && (
                                                <Button
                                                    variant="outline-danger"
                                                    size="sm"
                                                    type="button"
                                                    onClick={() => removeQuestion(idx)}
                                                >
                                                    Remove
                                                </Button>
                                            )}
                                        </div>
                                        <Row className="g-3">
                                            <Col md={6} sm={12}>
                                                <Form.Group>
                                                    <Form.Label>Question Text (English)</Form.Label>
                                                    <Form.Control
                                                        type="text"
                                                        value={q.questionText_i18n?.en || q.questionText || ""}
                                                        onChange={(e) =>
                                                            handleQuestionChange(idx, "questionTextEn", e.target.value)
                                                        }
                                                        required
                                                    />
                                                </Form.Group>
                                            </Col>
                                            <Col md={6} sm={12}>
                                                <Form.Group>
                                                    <Form.Label>Question Text (Hinglish)</Form.Label>
                                                    <Form.Control
                                                        type="text"
                                                        value={q.questionText_i18n?.hinglish || ""}
                                                        onChange={(e) =>
                                                            handleQuestionChange(idx, "questionTextHinglish", e.target.value)
                                                        }
                                                        placeholder="Optional"
                                                    />
                                                </Form.Group>
                                            </Col>

                                            <Col md={6} sm={12}>
                                                <Form.Group>
                                                    <Form.Label>Question Type</Form.Label>
                                                    <Form.Select
                                                        value={q.questionType}
                                                        onChange={(e) =>
                                                            handleQuestionChange(idx, "questionType", e.target.value)
                                                        }
                                                    >
                                                        {questionTypes.map((type) => (
                                                            <option key={type.value} value={type.value}>
                                                                {type.label}
                                                            </option>
                                                        ))}
                                                    </Form.Select>
                                                </Form.Group>
                                            </Col>

                                            <Col md={6} sm={12}>
                                                <Form.Group>
                                                    <Form.Label>Requirement</Form.Label>
                                                    <Form.Select
                                                        value={q.required !== false ? "required" : "optional"}
                                                        onChange={(e) =>
                                                            handleQuestionChange(
                                                                idx,
                                                                "required",
                                                                e.target.value === "required"
                                                            )
                                                        }
                                                    >
                                                        <option value="required">Required</option>
                                                        <option value="optional">Optional</option>
                                                    </Form.Select>
                                                </Form.Group>
                                            </Col>
                                        </Row>

                                        {["dropdown", "multiple-choice"].includes(q.questionType) && (
                                            <div className={styles.optionsWrap}>
                                                <Form.Label className={styles.optionsLabel}>Options (English + Hinglish)</Form.Label>
                                                <Row className={`g-2 ${styles.optionHeader}`}>
                                                    <Col md={5} sm={12}>English</Col>
                                                    <Col md={5} sm={12}>Hinglish</Col>
                                                    <Col md={2} sm={12}>Action</Col>
                                                </Row>

                                                {!q.optionRows.length && (
                                                    <div className={styles.emptyOptions}>No options yet. Add at least 2 options.</div>
                                                )}

                                                {q.optionRows.map((opt, oIdx) => (
                                                    <Row className={`g-2 mb-2 align-items-center ${styles.optionRow}`} key={oIdx}>
                                                        <Col md={5} sm={12}>
                                                            <Form.Control
                                                                type="text"
                                                                placeholder="English option"
                                                                value={opt.en}
                                                                onChange={(e) =>
                                                                    handleOptionChange(idx, oIdx, "en", e.target.value)
                                                                }
                                                            />
                                                        </Col>
                                                        <Col md={5} sm={12}>
                                                            <Form.Control
                                                                type="text"
                                                                placeholder="Hinglish option (optional)"
                                                                value={opt.hinglish}
                                                                onChange={(e) =>
                                                                    handleOptionChange(idx, oIdx, "hinglish", e.target.value)
                                                                }
                                                            />
                                                        </Col>
                                                        <Col md={2} sm={12}>
                                                            <Button
                                                                variant="outline-danger"
                                                                className={styles.optionRemoveBtn}
                                                                type="button"
                                                                onClick={() => removeOption(idx, oIdx)}
                                                                title="Remove option"
                                                            >
                                                                &times;
                                                            </Button>
                                                        </Col>
                                                    </Row>
                                                ))}
                                                <Button
                                                    variant="outline-primary"
                                                    size="sm"
                                                    type="button"
                                                    onClick={() => addOption(idx)}
                                                >
                                                    Add Option
                                                </Button>
                                            </div>
                                        )}
                                    </div>
                                ))}
                                <Button variant="outline-success" type="button" onClick={addQuestion}>
                                    Add Question
                                </Button>
                                <div className="mt-4">
                                    <Button type="submit" variant="dark" disabled={loading}>
                                        {loading ? "Creating..." : "Create Form"}
                                    </Button>
                                </div>
                            </Form>
                        </div>
                    </Col>
                    {toggleNotification && <Col md={4} sm={12}>
            <Notification />
          </Col>}
                </Row>
            </div>
        </React.Fragment>
    );
};

export default CreateForm;
